import React, { FormEvent, useState } from 'react';
import api, { getAccessToken } from '../api';
import { getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { useApiRequest } from '../useApiRequest';
import { validateFormSubmit } from '../utils/formValidation';
import { FormLabel } from './FormLabel';
import { Modal } from './Modal';

const CATEGORIES = [
  'ProhibitedItem',
  'StolenItem',
  'IntellectualProperty',
  'Fraud',
  'PersonalData',
  'HateOrViolence',
  'Other',
] as const;

interface ReportContentButtonProps {
  /** Reported listing; without it and `reportedUserId` the reporter enters a URL. */
  productId?: number;
  reportedUserId?: number;
  className?: string;
}

/** "Report illegal content" link opening the DSA Art. 16 notice form. */
export const ReportContentButton: React.FC<ReportContentButtonProps> = ({
  productId,
  reportedUserId,
  className = 'report-content-link',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {t('report.open')}
      </button>
      {open && (
        <ReportContentModal
          productId={productId}
          reportedUserId={reportedUserId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const ReportContentModal: React.FC<ReportContentButtonProps & { onClose: () => void }> = ({
  productId,
  reportedUserId,
  onClose,
}) => {
  const { t } = useTranslation();
  // Logged-in reporters are identified by their account; the confirmation goes to its email.
  const isLoggedIn = getAccessToken() != null;
  const needsUrl = productId == null && reportedUserId == null;
  const [category, setCategory] = useState('');
  const [contentUrl, setContentUrl] = useState(needsUrl ? window.location.href : '');
  const [explanation, setExplanation] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [goodFaith, setGoodFaith] = useState(false);
  const [reportId, setReportId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const { loading, call } = useApiRequest(async () => {
    const res = await api.post<{ id: number }>('/contentreports', {
      category,
      productId,
      reportedUserId,
      contentUrl: needsUrl ? contentUrl : null,
      explanation,
      reporterName: isLoggedIn ? null : name,
      reporterEmail: isLoggedIn ? null : email,
      goodFaithConfirmed: goodFaith,
    });
    setReportId(res.data.id);
  });

  const handleSubmit = async (e: FormEvent) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    setError('');
    try {
      await call();
    } catch (err) {
      setError(getErrorMessage(err, t('report.error')));
    }
  };

  return (
    <Modal open onClose={onClose} title={t('report.title')} panelClassName="modal--scroll">
      {reportId != null ? (
        <>
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            {t('report.success', { id: reportId })}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              {t('report.close')}
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <p className="text-muted">{t('report.intro')}</p>
          {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
          <div className="form-group">
            <FormLabel htmlFor="report-category" required>{t('report.category')}</FormLabel>
            <select
              id="report-category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              required
              disabled={loading}
            >
              <option value="" disabled>{t('report.category_placeholder')}</option>
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{t(`report.category.${c}`)}</option>
              ))}
            </select>
          </div>
          {needsUrl && (
            <div className="form-group">
              <FormLabel htmlFor="report-url" required>{t('report.url')}</FormLabel>
              <input
                id="report-url"
                type="url"
                value={contentUrl}
                onChange={e => setContentUrl(e.target.value)}
                required
                maxLength={2000}
                disabled={loading}
              />
            </div>
          )}
          <div className="form-group">
            <FormLabel htmlFor="report-explanation" required>{t('report.explanation')}</FormLabel>
            <textarea
              id="report-explanation"
              value={explanation}
              onChange={e => setExplanation(e.target.value)}
              placeholder={t('report.explanation_placeholder')}
              required
              maxLength={5000}
              rows={5}
              disabled={loading}
            />
          </div>
          {!isLoggedIn && (
            <>
              <div className="form-group">
                <FormLabel htmlFor="report-name" required>{t('report.name')}</FormLabel>
                <input
                  id="report-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  maxLength={200}
                  autoComplete="name"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <FormLabel htmlFor="report-email" required>{t('report.email')}</FormLabel>
                <input
                  id="report-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  maxLength={254}
                  autoComplete="email"
                  disabled={loading}
                />
              </div>
            </>
          )}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={goodFaith}
                onChange={e => setGoodFaith(e.target.checked)}
                required
                disabled={loading}
                aria-required="true"
                style={{ marginTop: '0.2rem' }}
              />
              <span>{t('report.good_faith')}</span>
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-accent" disabled={loading}>
              {loading && <span className="btn-spinner" aria-hidden="true" />}
              {t('report.submit')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              {t('rental.cancel')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};
